import LegalLayout, { LegalSection } from "../components/LegalLayout";
import { Link } from "react-router-dom";
import { BRAND } from "../data/content";
import { useLang, useT } from "../i18n/LanguageContext";

const COOKIES_EN = [
  ["PHPSESSID", "Necessary", "Preserves user session state across page requests.", "Session"],
  ["ASP.NET_SessionId", "Necessary", "Preserves user session state across page requests.", "Session"],
  ["CookieConsent", "Necessary", "Stores the user's cookie consent state for the current domain.", "3 months"],
  ["_ga", "Statistics", "Registers a unique ID used to generate statistical data on how the visitor uses the website.", "1 year"],
  ["_gat", "Statistics", "Used by Google Analytics to throttle request rate.", "1 day"],
  ["_gid", "Statistics", "Registers a unique ID used to generate statistical data on how the visitor uses the website.", "1 day"],
  ["SHR_ID", "Statistics", "Contains a visitor ID used to track navigation for internal website optimization.", "Persistent"],
  ["_fbp", "Marketing", "Used by Facebook to deliver a series of advertisement products such as real-time bidding.", "3 months"],
  ["fr", "Marketing", "Used by Facebook to deliver advertisement products from third-party advertisers.", "3 months"],
  ["tr", "Marketing", "Used by Facebook to deliver advertisement products from third-party advertisers.", "Session"],
  ["ads/ga-audiences", "Marketing", "Used by Google Ads to re-engage visitors likely to convert into customers.", "Session"],
];

const COOKIES_IT = [
  ["PHPSESSID", "Necessari", "Conserva lo stato della sessione utente tra le richieste di pagina.", "Sessione"],
  ["ASP.NET_SessionId", "Necessari", "Conserva lo stato della sessione utente tra le richieste di pagina.", "Sessione"],
  ["CookieConsent", "Necessari", "Memorizza lo stato del consenso ai cookie dell'utente per il dominio corrente.", "3 mesi"],
  ["_ga", "Statistici", "Registra un ID univoco utilizzato per generare dati statistici sull'utilizzo del sito da parte del visitatore.", "1 anno"],
  ["_gat", "Statistici", "Utilizzato da Google Analytics per limitare la frequenza delle richieste.", "1 giorno"],
  ["_gid", "Statistici", "Registra un ID univoco utilizzato per generare dati statistici sull'utilizzo del sito da parte del visitatore.", "1 giorno"],
  ["SHR_ID", "Statistici", "Contiene un ID visitatore utilizzato per tracciare la navigazione ai fini dell'ottimizzazione interna del sito.", "Persistente"],
  ["_fbp", "Marketing", "Utilizzato da Facebook per erogare prodotti pubblicitari come le offerte in tempo reale.", "3 mesi"],
  ["fr", "Marketing", "Utilizzato da Facebook per erogare prodotti pubblicitari di inserzionisti terzi.", "3 mesi"],
  ["tr", "Marketing", "Utilizzato da Facebook per erogare prodotti pubblicitari di inserzionisti terzi.", "Sessione"],
  ["ads/ga-audiences", "Marketing", "Utilizzato da Google Ads per re-ingaggiare i visitatori con maggiore probabilità di conversione.", "Sessione"],
];

export default function CookiePolicy() {
  const { lang } = useLang();
  const t = useT();
  const it = lang === "it";
  const cookies = it ? COOKIES_IT : COOKIES_EN;
  const headers = it ? ["Cookie", "Tipo", "Descrizione", "Durata"] : ["Cookie", "Type", "Description", "Duration"];
  return (
    <LegalLayout
      title="Cookie Policy"
      subtitle={it
        ? "Redatta ai sensi dell'art. 122 del D.lgs. 196/2003, del Regolamento UE 2016/679 e delle Linee guida del Garante per la protezione dei dati personali del 10 giugno 2021."
        : "Issued pursuant to Art. 122 of Italian Legislative Decree 196/2003, EU Regulation 2016/679, and the Italian Data Protection Authority's Guidelines of 10 June 2021."}
      seoTitle={t("seo.cookie.title")}
      seoDesc={t("seo.cookie.desc")}
      path="/cookie-policy"
    >
      <LegalSection title={it ? "Titolare del trattamento" : "Data Controller"}>
        {it ? (
          <p>Il Titolare del trattamento è <strong>{BRAND.legalName}</strong>, {BRAND.address}, P.IVA {BRAND.vat}. Per
            qualsiasi richiesta puoi contattarci all'indirizzo <a href={`mailto:${BRAND.email}`} className="text-[#C5A059] underline underline-offset-2">{BRAND.email}</a>. Consulta anche la nostra <Link to="/privacy" className="text-[#C5A059] underline underline-offset-2">Privacy Policy</Link>.</p>
        ) : (
          <p>The Data Controller is <strong>{BRAND.legalName}</strong>, {BRAND.address}, VAT {BRAND.vat}. For
            any request you may contact us at <a href={`mailto:${BRAND.email}`} className="text-[#C5A059] underline underline-offset-2">{BRAND.email}</a>. See also our <Link to="/privacy" className="text-[#C5A059] underline underline-offset-2">Privacy Policy</Link>.</p>
        )}
      </LegalSection>

      <LegalSection title={it ? "Cosa sono i cookie?" : "What are cookies?"}>
        {it ? (
          <p>I cookie sono piccoli file di testo utilizzati per memorizzare piccole informazioni. Vengono salvati
            sul tuo dispositivo quando il sito viene caricato nel browser. I cookie ci aiutano a far funzionare
            correttamente il sito, a renderlo più sicuro, a offrire un'esperienza migliore e a comprendere le
            prestazioni del sito, così da analizzare ciò che funziona e ciò che va migliorato.</p>
        ) : (
          <p>Cookies are small text files used to store small pieces of information. They are stored on your
            device when the website is loaded in your browser. Cookies help us make the website work properly,
            make it more secure, provide a better user experience, and understand how the website performs so
            we can analyse what works and what needs improvement.</p>
        )}
      </LegalSection>

      <LegalSection title={it ? "Quali tipi di cookie esistono?" : "What types of cookies exist?"}>
        {it ? (
          <>
            <p><strong>Per durata:</strong> cookie di sessione (salvati temporaneamente ed eliminati alla chiusura
              del browser) e cookie persistenti (che restano per un periodo definito dopo la chiusura del browser).</p>
            <p><strong>Per funzione:</strong> cookie tecnici (funzionali al corretto utilizzo del sito); cookie
              analitici (per monitorare e analizzare la navigazione); e cookie di profilazione (per creare profili
              utente, generalmente per inviare pubblicità in linea con le preferenze dell'utente).</p>
            <p><strong>Per origine:</strong> cookie di prima parte (installati dal sito che stai visitando) e cookie
              di terze parti (installati da un'organizzazione diversa dal proprietario del sito).</p>
          </>
        ) : (
          <>
            <p><strong>By duration:</strong> session cookies (saved temporarily and deleted when the browser is
              closed) and persistent cookies (which remain for a set period after the browser is closed).</p>
            <p><strong>By function:</strong> technical cookies (functional to the correct use of the site);
              analytical cookies (to monitor and analyse navigation); and profiling cookies (to create user
              profiles, generally to send advertising in line with the user's preferences).</p>
            <p><strong>By origin:</strong> first-party cookies (installed by the site you are visiting) and
              third-party cookies (installed by an organisation other than the site owner).</p>
          </>
        )}
      </LegalSection>

      <LegalSection title={it ? "Principali cookie utilizzati su questo sito" : "Main cookies used on this site"}>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs md:text-sm">
            <thead>
              <tr className="text-[#C5A059] border-b border-white/20">
                {headers.map((h) => (
                  <th key={h} className="py-3 pr-4 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cookies.map((c) => (
                <tr key={c[0]} className="border-b border-white/10 align-top">
                  <td className="py-3 pr-4 text-white/90 whitespace-nowrap">{c[0]}</td>
                  <td className="py-3 pr-4 text-white/60 whitespace-nowrap">{c[1]}</td>
                  <td className="py-3 pr-4 text-white/60">{c[2]}</td>
                  <td className="py-3 text-white/60 whitespace-nowrap">{c[3]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LegalSection>

      <LegalSection title={it ? "Come posso gestire le preferenze sui cookie?" : "How can I control cookie preferences?"}>
        {it ? (
          <>
            <p>Puoi gestire i cookie tramite le impostazioni del browser, accettandoli o rifiutandoli oppure
              richiedendo un avviso prima che un cookie venga impostato. Disabilitare completamente i cookie
              potrebbe impedirti di utilizzare tutte le funzionalità interattive. Ogni browser (Chrome, Edge,
              Firefox, Opera, Safari) fornisce le proprie istruzioni per eliminare o disabilitare i cookie.</p>
            <p>Puoi inoltre attivare l'impostazione anti-tracciamento del browser e trovare maggiori informazioni
              sulla gestione dei cookie di terze parti o di marketing sul sito YourOnlineChoices.</p>
            <p>Puoi esprimere o modificare il tuo consenso tramite il banner mostrato alla prima visita, oppure
              cancellando i cookie di questo sito dal browser per essere nuovamente interpellato.</p>
          </>
        ) : (
          <>
            <p>You can manage cookies via your browser settings, accepting or rejecting cookies or requesting a
              warning before a cookie is set. Note that completely disabling cookies may prevent you from using
              all interactive features. Each browser (Chrome, Edge, Firefox, Opera, Safari) provides its own
              instructions for deleting or disabling cookies.</p>
            <p>You can also enable the anti-tracking setting in your browser, and find more information about
              managing third-party or marketing cookies on the YourOnlineChoices website.</p>
            <p>You may express or change your consent through the banner shown on your first visit, or by clearing
              the cookies for this site in your browser to be prompted again.</p>
          </>
        )}
      </LegalSection>

      <LegalSection title={it ? "Cookie di terze parti" : "Third-party cookies"}>
        {it ? (
          <>
            <p>Il sito agisce anche da intermediario per cookie di terze parti (come i pulsanti dei social network)
              per fornire servizi e funzionalità aggiuntive, o pubblicità personalizzata. Il sito non ha alcun
              controllo sui cookie di terze parti né accesso alle informazioni raccolte tramite essi. Il sito può
              utilizzare cookie dei seguenti soggetti terzi:</p>
            <p><strong>Google Analytics</strong> (Google Inc.) — analisi web con anonimizzazione dell'IP. Luogo del
              trattamento: USA.</p>
            <p><strong>Facebook</strong> (Meta Platforms, Inc.) — plug-in social e pubblicità. Luogo del
              trattamento: USA.</p>
            <p><strong>Twitter, LinkedIn, YouTube</strong> — interazione social e condivisione video. Per YouTube è
              attiva l'opzione "privacy avanzata (senza cookie)". Luogo del trattamento: USA.</p>
          </>
        ) : (
          <>
            <p>The site also acts as an intermediary for third-party cookies (such as social-network buttons) to
              provide additional services and features, or personalised advertising. This site has no control
              over third-party cookies and no access to the information collected through them. The site may use
              cookies from the following third parties:</p>
            <p><strong>Google Analytics</strong> (Google Inc.) — web analysis with IP anonymisation. Place of
              processing: USA.</p>
            <p><strong>Facebook</strong> (Meta Platforms, Inc.) — social plug-ins and advertising. Place of
              processing: USA.</p>
            <p><strong>Twitter, LinkedIn, YouTube</strong> — social interaction and video sharing. For YouTube,
              the "advanced privacy (no cookie)" option is enabled. Place of processing: USA.</p>
          </>
        )}
      </LegalSection>

      <LegalSection title={it ? "I tuoi diritti" : "Your rights"}>
        {it ? (
          <p>Puoi esercitare i tuoi diritti di revoca del consenso (art. 7), accesso (art. 15), rettifica (art. 16),
            cancellazione (art. 17), limitazione (art. 18), portabilità (art. 20) e opposizione (art. 21) contattando
            il Titolare del trattamento all'indirizzo {BRAND.email}. Tutti i dettagli sono disponibili nella nostra{" "}
            <Link to="/privacy" className="text-[#C5A059] underline underline-offset-2">Privacy Policy</Link>.</p>
        ) : (
          <p>You may exercise your rights to withdraw consent (Art. 7), access (Art. 15), rectification (Art. 16),
            erasure (Art. 17), restriction (Art. 18), portability (Art. 20) and objection (Art. 21) by contacting
            the Data Controller at {BRAND.email}. Full details are available in our{" "}
            <Link to="/privacy" className="text-[#C5A059] underline underline-offset-2">Privacy Policy</Link>.</p>
        )}
      </LegalSection>
    </LegalLayout>
  );
}
